import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createResidenceType,
  CreateResidenceTypeRequest,
  CreatedResponse,
  ResidenceCategoryModel,
  RoofTypeModel,
  uploadImageForResidenceType,
  getResidenceCategoryList,
  getRoofTypeList,
  getAntiForgeryToken,
} from "../../clients/BackOfficeApi";
import { useNavigate } from "react-router-dom";
import { AttachmentIcon, PlusSquareIcon } from "@chakra-ui/icons";
import {
  isApiError,
  isBadRequest,
  ValidationErrorModel,
} from "../../utils/apiUtils";
import { getDynamicMessageId } from "../I18n";
import { useIntl } from "react-intl";
import { ErrorList } from "../../elements/ErrorList";
import { Dropdown } from "../Dropdown";
import { ImageInput } from "../ImageInput";
import { ConfirmCloseDirtyDialog } from "./ConfirmCloseDirtyDialog";

const initialResidenceTypeState: Readonly<
  Required<CreateResidenceTypeRequest>
> = Object.freeze({
  name: "",
  residenceCategoryId: 0,
  roofTypeId: 0,
});

export function CreateResidenceTypeModal() {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const [closingDirty, setDiscardPrompt] = useState(false);
  const [residenceCategories, setResidenceCategories] = useState<
    ResidenceCategoryModel[]
  >([]);
  const [roofTypes, setRoofTypes] = useState<RoofTypeModel[]>([]);
  const [imageFile, setImageFile] = useState<File>();
  const [, setImageFileUrl] = useState<string>();
  const [validationError, setValidationError] =
    useState<ValidationErrorModel<CreateResidenceTypeRequest & File>>();
  const [error, setError] = useState<string>();
  const [createResidenceTypeModel, setCreateResidenceTypeModel] =
    useState<CreateResidenceTypeRequest>(initialResidenceTypeState);
  const navigate = useNavigate();
  const intl = useIntl();

  useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setImageFileUrl(url);

      return () => {
        setImageFileUrl(undefined);
        URL.revokeObjectURL(url);
      };
    }
  }, [imageFile]);

  const isValid = useMemo(
    () =>
      [
        createResidenceTypeModel.name.trim(),
        createResidenceTypeModel.residenceCategoryId > 0,
        createResidenceTypeModel.roofTypeId > 0,
        imageFile?.type.startsWith("image/"),
      ].every(Boolean),
    [createResidenceTypeModel, imageFile],
  );

  const isDirty = useMemo(
    () =>
      [
        createResidenceTypeModel.name.trim(),
        createResidenceTypeModel?.residenceCategoryId,
        createResidenceTypeModel?.roofTypeId,
        imageFile,
      ].some(Boolean),
    [createResidenceTypeModel, imageFile],
  );

  const onDiscard = useCallback(
    (confirmed: boolean) => {
      setDiscardPrompt(false);

      if (!confirmed) {
        return;
      }

      setCreateResidenceTypeModel(initialResidenceTypeState);
      setImageFile(undefined);
      setValidationError(undefined);
      setError(undefined);
      onClose();
    },
    [onClose],
  );

  const onCloseGuard = useCallback(() => {
    setDiscardPrompt(isDirty);

    if (!isDirty) onDiscard(true);
  }, [onDiscard, isDirty]);

  useEffect(() => {
    const abortController = new AbortController();

    getResidenceCategoryList({
      query: {
        Offset: 0,
        Limit: 100,
      },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (abortController.signal.aborted) return;

        if (data) {
          setResidenceCategories(data.items ?? []);
        }
      })
      .catch((e) => {
        if (abortController.signal.aborted) return;

        throw e;
      });

    return () => abortController.abort("Component unmounted or re-rendered");
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    getRoofTypeList({
      query: {
        Offset: 0,
        Limit: 100,
      },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (abortController.signal.aborted) return;

        if (data) {
          setRoofTypes(data.items ?? []);
        }
      })
      .catch((e) => {
        if (abortController.signal.aborted) return;

        throw e;
      });

    return () => abortController.abort("Component unmounted or re-rendered");
  }, []);

  const create = useCallback(async () => {
    if (!createResidenceTypeModel || !imageFile || !isValid) return;

    setValidationError(undefined);
    setError(undefined);

    try {
      const { data: antiForgeryToken } = await getAntiForgeryToken({
        credentials: "include",
        throwOnError: true,
      });

      const createResidenceTypeResult = await createResidenceType({
        body: createResidenceTypeModel,
        throwOnError: true,
      });

      if (createResidenceTypeResult.response.status === 201) {
        const createdResponse =
          createResidenceTypeResult.data as CreatedResponse;

        await uploadImageForResidenceType({
          path: { residenceTypeId: createdResponse.id },
          body: { file: imageFile },
          headers: {
            "X-XSRF-TOKEN": antiForgeryToken,
          },
          credentials: "include",
          throwOnError: true,
        });

        navigate(`/residence-types/${createdResponse.id}`);
        onClose();
      }
    } catch (e) {
      if (isApiError(e)) {
        const errorId = getDynamicMessageId(e.title ?? "Unknown error");

        setError(intl.formatMessage({ id: errorId, defaultMessage: e.title ?? "Unknown error" }));

        if (isBadRequest(e)) {
          setValidationError(e.errors);
        }
      } else {
        setError(`Fout bij het aanmaken van het woningtype: ${e}`);
      }
    }
  }, [createResidenceTypeModel, imageFile, navigate, onClose, intl, isValid]);

  return (
    <>
      <Button onClick={onOpen}>
        <PlusSquareIcon mr={2} />
        Nieuw woningtype
      </Button>
      <Modal closeOnOverlayClick isOpen={isOpen} onClose={onCloseGuard}>
        <ModalOverlay />
        <ModalContent onSubmit={create}>
          <ConfirmCloseDirtyDialog
            isOpen={closingDirty}
            onConfirm={onDiscard}
          />
          <ModalHeader>Nieuw woningtype aanmaken</ModalHeader>
          <ModalCloseButton />
          <ModalBody
            as="form"
            onSubmit={(e) => {
              e.preventDefault();
              create();
            }}
          >
            {!!error && <Text color="red.500">{error}</Text>}
            <FormControl isInvalid={!!validationError?.Name} isRequired>
              <FormLabel>Naam</FormLabel>
              <Input
                isInvalid={!!validationError?.Name}
                onChange={(e) =>
                  setCreateResidenceTypeModel((c) => ({
                    ...c,
                    name: e.target.value,
                  }))
                }
                placeholder="Voer de naam van het woningtype in"
                value={createResidenceTypeModel?.name ?? ""}
              />
              <FormErrorMessage>{validationError?.Name}</FormErrorMessage>
            </FormControl>
            <ImageInput
              input={{ required: true }}
              noConfirm
              onChange={(files) => setImageFile(files[0])}
              title="Afbeelding"
            >
              <AttachmentIcon mr={1} />
              Selecteer afbeelding
            </ImageInput>
            <Dropdown
              isRequired
              items={residenceCategories}
              onChange={(selected) =>
                setCreateResidenceTypeModel((c) => ({
                  ...c,
                  residenceCategoryId: selected[0]?.id ?? 0,
                }))
              }
              title="Woningcategorie"
              value={residenceCategories.filter(
                (c) => c.id === createResidenceTypeModel?.residenceCategoryId,
              )}
              valueDisplay={(selected) =>
                selected[0]?.name ? (
                  <Text>{selected[0].name}</Text>
                ) : (
                  <Text color="gray.500">Kies een woningcategorie</Text>
                )
              }
            />
            <ErrorList errors={validationError?.ResidenceCategoryId} />
            <Dropdown
              isRequired
              items={roofTypes}
              onChange={(selected) =>
                setCreateResidenceTypeModel((c) => ({
                  ...c,
                  roofTypeId: selected[0]?.id ?? 0,
                }))
              }
              title="Daktype"
              value={roofTypes.filter(
                (c) => c.id === createResidenceTypeModel?.roofTypeId,
              )}
              valueDisplay={(selected) =>
                selected[0]?.name ? (
                  <Text>{selected[0].name}</Text>
                ) : (
                  <Text color="gray.500">Kies een daktype</Text>
                )
              }
            />
            <ErrorList errors={validationError?.RoofTypeId} />
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              isDisabled={!isValid}
              mr={3}
              onClick={create}
            >
              Opslaan
            </Button>
            <Button onClick={onCloseGuard} variant="ghost">
              Annuleren
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
